export type SessionStatus = 'READY' | 'WAITS-NICK' | 'WAITS-TIME' | 'TAKEN' | 'DONE' | 'DROPPED';
export type ProjectStatus = 'PLANNED' | 'ACTIVE' | 'STALLED' | 'WAITING-NICK' | 'DONE' | 'PARKED';
export type ScopeState = 'OPEN' | 'DONE' | 'DROPPED';
export type WaitKind = 'nick' | 'time' | 'pr' | 'row';

export interface TakenBy { readonly dev: string; readonly window?: string; readonly at: string }

export interface DocketSession {
  readonly id: string;
  readonly projectId: string;
  readonly title: string;
  readonly starterPath?: string;
  readonly tier: 0 | 1 | 2 | 3;
  readonly status: SessionStatus;
  readonly dependsOn: readonly string[];
  readonly earliestAt?: string;
  readonly takenBy?: TakenBy;
  readonly result?: { readonly found: number; readonly fixed: number; readonly prs: readonly string[] };
  readonly moneyOrCustomerData?: boolean;
  readonly nickPicked?: boolean;
  readonly userBlocking?: boolean;
  readonly areaSize?: number;
}

export interface RemainingScopeItem {
  readonly item: string;
  readonly state: ScopeState;
  readonly sessionId?: string;
  readonly nickItemId?: string;
}

export interface LiveVerified { readonly n: number; readonly of: number; readonly control: string; readonly readAt: string }

export interface DocketProject {
  readonly id: string;
  readonly title: string;
  readonly status: ProjectStatus;
  readonly built: readonly string[];
  readonly liveVerified?: LiveVerified;
  readonly remainingScope: readonly RemainingScopeItem[];
  readonly waitsOn: readonly { readonly kind: WaitKind; readonly ref: string }[];
  readonly lastProgressAt?: string;
}

export interface ProjectUpdate {
  readonly built?: readonly string[];
  readonly liveVerified?: LiveVerified;
  readonly remainingScope?: readonly RemainingScopeItem[];
  readonly waitsOn?: DocketProject['waitsOn'];
  readonly unchangedReason?: string;
}

const TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|[+-](\d{2}):(\d{2}))$/;

/**
 * A time the docket will trust: a full ISO date-time WITH a zone (Z or +hh:mm), that exists on the
 * calendar. Never a bare year, "tomorrow", a zone-less time (read in the server's local zone), or
 * a date the engine silently rolls over (Feb 31, hour 24).
 */
export function isValidTime(iso: unknown): iso is string {
  if (typeof iso !== 'string') return false;
  const m = TIME.exec(iso);
  if (!m) return false;
  const [y, mo, d, h, mi, sec] = [m[1], m[2], m[3], m[4], m[5], m[6] ?? '0'].map(Number);
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d) return false;
  if (h > 23 || mi > 59 || sec > 59) return false;
  if (m[8] !== undefined && (Number(m[8]) > 23 || Number(m[9]) > 59)) return false;
  return !Number.isNaN(Date.parse(iso));
}
